/**
 * Pricing configuration — the ONLY place prices, coupons, campaigns,
 * scholarships, partners and regions are declared.
 *
 * Changing a price means editing this file. No calculation lives here.
 */

import type {
  DiscountRule,
  RegionCode,
  RegionConfig,
} from "./pricing-types";

/** Pricing Policy version. Bump on any change to numbers or precedence. */
export const PRICING_VERSION = "2.0.0";

/** Default region when a caller does not say. */
export const DEFAULT_REGION: RegionCode = "IN";

/**
 * Regions. India is live. The rest are declared so a launch is configuration,
 * not a code change — `active: false` keeps them out of every lookup today.
 *
 * FX is deliberately NOT implemented: each region carries its own list price
 * (`fxToInr: null`). When FX arrives, set a rate and the engine can derive.
 */
export const REGIONS: Record<RegionCode, RegionConfig> = {
  IN: {
    code: "IN",
    label: "India",
    currency: "INR",
    listPrice: 4999,
    // India MRP is GST-inclusive — the buyer pays exactly the MRP.
    taxRatePercent: 18,
    taxInclusive: true,
    taxLabel: "GST (included)",
    gateway: "razorpay",
    allowedLayers: ["campaign", "scholarship", "coupon", "partner", "institution"],
    fxToInr: null,
    active: true,
  },
  US: {
    code: "US",
    label: "International",
    currency: "USD",
    // Canonical international list price. Every PayPal order starts here.
    listPrice: 499,
    taxRatePercent: 0,
    taxInclusive: true,
    taxLabel: "Tax",
    gateway: "paypal",
    // Same layers as India. USD discounts come from each rule's
    // `regionOverrides.US`, never from its rupee value.
    allowedLayers: ["campaign", "scholarship", "coupon", "partner", "institution"],
    fxToInr: null,
    active: true,
  },
  ME: {
    code: "ME",
    label: "Middle East",
    currency: "AED",
    listPrice: 549,
    taxRatePercent: 5,
    taxInclusive: false,
    taxLabel: "VAT",
    gateway: "paypal",
    allowedLayers: ["campaign", "scholarship", "coupon", "partner", "institution"],
    fxToInr: null,
    active: false,
  },
  AF: {
    code: "AF",
    label: "Africa",
    currency: "ZAR",
    listPrice: 2699,
    taxRatePercent: 15,
    taxInclusive: false,
    taxLabel: "VAT",
    gateway: "paypal",
    allowedLayers: ["campaign", "scholarship", "coupon", "partner", "institution"],
    fxToInr: null,
    active: false,
  },
  EU: {
    code: "EU",
    label: "Europe",
    currency: "EUR",
    listPrice: 139,
    taxRatePercent: 20,
    taxInclusive: false,
    taxLabel: "VAT",
    gateway: "paypal",
    allowedLayers: ["campaign", "scholarship", "coupon", "partner", "institution"],
    fxToInr: null,
    active: false,
  },
  AU: {
    code: "AU",
    label: "Australia",
    currency: "AUD",
    listPrice: 229,
    taxRatePercent: 10,
    taxInclusive: false,
    taxLabel: "GST",
    gateway: "paypal",
    allowedLayers: ["campaign", "scholarship", "coupon", "partner", "institution"],
    fxToInr: null,
    active: false,
  },
};

/**
 * Campaigns — time-boxed offers that are NOT coupons.
 *
 * `autoApply: false` on every campaign today, which is what keeps behaviour
 * identical to before this engine landed: the launch price is reached by
 * typing the INDEPENDENCE coupon, not automatically. Flip `autoApply` to true
 * to make a campaign price the default for its window.
 */
export const CAMPAIGNS: DiscountRule[] = [
  {
    id: "INDEPENDENCE_2026",
    label: "Launch Offer",
    layer: "campaign",
    type: "flat",
    value: 1000, // ₹4,999 → ₹3,999
    validFrom: "2026-08-08T00:00:00+05:30",
    validUntil: "2026-09-30T23:59:59+05:30",
    regions: ["IN", "US"],
    autoApply: false,
    stackable: false,
    category: "launch",
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 20, label: "Launch Offer — $399" } },
  },
  {
    id: "BACK_TO_SCHOOL",
    label: "Back to School",
    layer: "campaign",
    type: "percent",
    value: 15,
    validFrom: "2026-06-01T00:00:00+05:30",
    validUntil: "2026-07-15T23:59:59+05:30",
    regions: ["IN"],
    autoApply: false,
    stackable: false,
    category: "seasonal",
    active: false,
  },
  {
    id: "BLACK_FRIDAY",
    label: "Black Friday",
    layer: "campaign",
    type: "percent",
    value: 25,
    validFrom: "2026-11-24T00:00:00+05:30",
    validUntil: "2026-12-01T23:59:59+05:30",
    autoApply: false,
    stackable: false,
    category: "seasonal",
    active: false,
  },
  {
    id: "CHRISTMAS",
    label: "Christmas & New Year",
    layer: "campaign",
    type: "percent",
    value: 20,
    validFrom: "2026-12-20T00:00:00+05:30",
    validUntil: "2027-01-05T23:59:59+05:30",
    autoApply: false,
    stackable: false,
    category: "seasonal",
    active: false,
  },
];

/**
 * Scholarships — need-based and equity pricing. Redeemed with a code today,
 * so they also appear in `COUPONS` with `layer: "scholarship"`.
 */
export const SCHOLARSHIPS: DiscountRule[] = [
  {
    id: "NRECT50LOVE",
    label: "Student Scholarship — ₹2,499",
    layer: "scholarship",
    type: "flat",
    value: 2500,
    regions: ["IN", "US"],
    category: "merit",
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 50, label: "Student Scholarship — $249" } },
  },
  {
    id: "NRECT50LOVEGALS",
    label: "Girls & Women Future Scholarship — ₹1,249.50",
    layer: "scholarship",
    type: "flat",
    value: 3749.5,
    regions: ["IN", "US"],
    category: "equity",
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 75, label: "Girls & Women Future Scholarship — $125" } },
  },
];

/**
 * Partners — schools, universities, corporates, government, NGOs, affiliates
 * and resellers. Add a partner by adding an entry.
 */
export const PARTNERS: DiscountRule[] = [
  {
    id: "VKITPTNRSCL",
    label: "School Partnership — ₹3,499",
    layer: "partner",
    type: "flat",
    value: 1500,
    partnerOrg: "Partner schools",
    category: "school",
    regions: ["IN"],
    active: true,
  },
  {
    id: "VKITPTNRKID",
    label: "Partner Kids — ₹1,499",
    layer: "partner",
    type: "flat",
    value: 3500,
    partnerOrg: "Partner staff families",
    category: "corporate",
    regions: ["IN", "US"],
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 70, label: "Partner Kids — $150" } },
  },
  {

    id: "VKITPTNRINTLKID",
    label: "WeKIT Special Partner Price — $150",
    layer: "partner",
    type: "percent",
    value: 70,
    partnerOrg: "Partner staff families (international)",
    category: "corporate",
    validFrom: "2026-08-08T00:00:00+05:30",
    regions: ["US"],
    active: true,
  },
];


/**
 * Coupon catalogue — every code a buyer can type, with its layer.
 *
 * The `coupons` table remains authoritative at checkout (it holds redemption
 * counts and can be edited by admin without a deploy). This catalogue is the
 * declared intent, used by the UI, by reporting and as the fallback when no
 * database row is supplied.
 */
export const COUPONS: DiscountRule[] = [
  {
    id: "MOBLIGHTOFJOY",
    label: "Moms of Bangalore — 50% off",
    layer: "partner",
    type: "percent",
    value: 50,
    validFrom: "2026-09-12T00:00:00+05:30",
    validUntil: "2026-10-31T23:59:59+05:30",
    regions: ["IN", "US"],
    stackable: false,
    category: "partner",
    partnerOrg: "Moms of Bangalore",
    active: true,
    regionOverrides: {
      US: { type: "percent", value: 50, label: "Moms of Bangalore — 50% off" },
    },
  },
  {
    id: "FESTIVAL80",
    label: "Festival Offer — 80% off",
    layer: "campaign",
    type: "percent",
    value: 80,
    validFrom: "2026-10-15T00:00:00+05:30",
    validUntil: "2026-11-15T23:59:59+05:30",
    regions: ["IN", "US"],
    stackable: false,
    category: "campaign",
    active: true,
    regionOverrides: { US: { type: "percent", value: 80, label: "Festival Offer — 80% off" } },
  },
  {
    id: "FESTIVAL30",
    label: "Festival Offer — 30% off",
    layer: "campaign",
    type: "percent",
    value: 30,
    validFrom: "2026-09-01T00:00:00+05:30",
    validUntil: "2026-09-30T23:59:59+05:30",
    regions: ["IN", "US"],
    stackable: false,
    category: "campaign",
    active: true,
    regionOverrides: { US: { type: "percent", value: 30, label: "Festival Offer — 30% off" } },
  },
  {
    id: "IND80",
    label: "Extended by Popular Demand — 80% off",
    layer: "campaign",
    type: "percent",
    value: 80,
    validFrom: "2026-08-14T00:00:00+05:30",
    validUntil: "2026-08-20T23:59:59+05:30",
    regions: ["IN", "US"],
    stackable: false,
    category: "campaign",
    active: true,
    regionOverrides: { US: { type: "percent", value: 80, label: "Extended by Popular Demand — 80% off" } },
  },
  {
    id: "IND79",
    label: "Flash Offer — 79% off",
    layer: "campaign",
    type: "percent",
    value: 79,
    validFrom: "2026-08-14T00:00:00+05:30",
    validUntil: "2026-08-16T23:59:59+05:30",
    regions: ["IN", "US"],
    stackable: false,
    category: "campaign",
    active: true,
    regionOverrides: { US: { type: "percent", value: 79, label: "Flash Offer — $105" } },
  },
  {

    id: "INDEPENDENCE",
    label: "Launch Offer — ₹3,999",
    layer: "campaign",
    type: "flat",
    value: 1000,
    validFrom: "2026-08-08T00:00:00+05:30",
    validUntil: "2026-09-30T23:59:59+05:30",
    regions: ["IN", "US"],
    stackable: false,
    category: "campaign",
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 20, label: "Launch Offer — $399" } },
  },
  {
    id: "VKITLOVE",
    label: "Student Offer — ₹3,499",
    layer: "coupon",
    type: "flat",
    value: 1500,
    validFrom: "2026-08-08T00:00:00+05:30",
    validUntil: "2026-09-30T23:59:59+05:30",
    regions: ["IN", "US"],
    stackable: false,
    category: "student",
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 30, label: "Student Offer — $349" } },
  },
  {
    id: "NRECT50LOVE",
    label: "Student Scholarship — ₹2,499",
    layer: "scholarship",
    type: "flat",
    value: 2500,
    regions: ["IN", "US"],
    stackable: false,
    category: "scholarship",
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 50, label: "Student Scholarship — $249" } },
  },
  {
    id: "NRECT50LOVEGALS",
    label: "Girls & Women Future Scholarship — ₹1,249.50",
    layer: "scholarship",
    type: "flat",
    value: 3749.5,
    regions: ["IN", "US"],
    stackable: false,
    category: "scholarship",
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 75, label: "Girls & Women Future Scholarship — $125" } },
  },
  {
    id: "VKITFNF",
    label: "Friends & Family — ₹2,999",
    layer: "coupon",
    type: "flat",
    value: 2000,
    validUntil: "2026-10-31T23:59:59+05:30",
    regions: ["IN", "US"],
    stackable: false,
    category: "community",
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 40, label: "Friends & Family — $299" } },
  },
  {
    id: "VKITPTNRKID",
    label: "Partner Kids — ₹1,499",
    layer: "partner",
    type: "flat",
    value: 3500,
    regions: ["IN", "US"],
    stackable: false,
    category: "partner",
    partnerOrg: "Partner staff families",
    active: true,
    // USD is a percentage off the $499 MRP, not an FX conversion.
    regionOverrides: { US: { type: "percent", value: 70, label: "Partner Kids — $150" } },
  },
  {
    id: "VKITPTNRINTLKID",
    label: "WeKIT Special Partner Price — $150",
    layer: "partner",
    type: "percent",
    value: 70,
    validFrom: "2026-08-08T00:00:00+05:30",
    regions: ["US"],
    stackable: false,
    category: "partner",
    partnerOrg: "Partner staff families (international)",
    active: true,
  },

  {
    id: "VKITPTNRSCL",
    label: "School Partnership — ₹3,499",
    layer: "partner",
    type: "flat",
    value: 1500,
    regions: ["IN"],
    stackable: false,
    category: "partner",
    partnerOrg: "Partner schools",
    active: true,
  },
  {
    id: "REF-WEKITEER",
    label: "Friend referral — ₹100 off",
    layer: "coupon",
    type: "flat",
    value: 100,
    regions: ["IN"],
    stackable: false,
    category: "referral",
    partnerOrg: "Referral",
    active: true,
  },
  {
    id: "TEST99",
    label: "Testing — ₹49",
    layer: "coupon",
    type: "flat",
    value: 4950,
    regions: ["IN"],
    stackable: false,
    internalOnly: false,
    category: "internal",
    active: true,
  },
  {
    id: "WCC3604INDIA",
    label: "Senior Industry Invite — complimentary access",
    layer: "coupon",
    type: "full",
    value: 100,
    validUntil: "2027-03-31T23:59:59+05:30",
    regions: ["IN"],
    stackable: false,
    category: "senior_industry_invite",
    maxRedemptions: 100,
    active: true,
  },
  {
    id: "BLESS100",
    label: "BLESS100 — complimentary access",
    layer: "coupon",
    type: "full",
    value: 100,
    validUntil: "2027-03-31T23:59:59+05:30",
    regions: ["IN", "US"],
    stackable: false,
    category: "complimentary",
    maxRedemptions: 100,
    active: true,
  },

  // ---- NRECT scholarship ladder -----------------------------------------
  // Issued by the NRECT team, who work out which one a family qualifies for.
  // Each code already carries the whole award, so nothing is added up here and
  // no award can exceed 100%. Percentages, so they price in every currency.
  {
    id: "FULLSCHOLAR100",
    label: "Full Scholarship — 100%",
    layer: "scholarship",
    type: "percent",
    value: 100,
    stackable: false,
    category: "scholarship",
    requiresApproval: true,
    active: true,
  },
  {
    id: "IMPACT75",
    label: "Impact Scholarship — 75% off",
    layer: "scholarship",
    type: "percent",
    value: 75,
    stackable: false,
    category: "scholarship",
    active: true,
  },
  {
    id: "DREAM50",
    label: "Dream Scholarship — 50% off",
    layer: "scholarship",
    type: "percent",
    value: 50,
    stackable: false,
    category: "scholarship",
    active: true,
  },
  {
    id: "FUTURE30",
    label: "Future Scholarship — 30% off",
    layer: "scholarship",
    type: "percent",
    value: 30,
    stackable: false,
    category: "scholarship",
    active: true,
  },
  {
    id: "GIRLRISE",
    label: "Girl Rise Award — 15% off",
    layer: "scholarship",
    type: "percent",
    value: 15,
    stackable: false,
    category: "scholarship",
    active: true,
  },
  {
    id: "NGOPARTNER",
    label: "NGO Partner Award — 20% off",
    layer: "scholarship",
    type: "percent",
    value: 20,
    stackable: false,
    category: "scholarship",
    active: true,
  },
  {
    id: "MERIT10",
    label: "Merit Award — 10% off",
    layer: "scholarship",
    type: "percent",
    value: 10,
    stackable: false,
    category: "scholarship",
    active: true,
  },
  {
    id: "FIRSTGEN",
    label: "First-Generation Learner Award — 10% off",
    layer: "scholarship",
    type: "percent",
    value: 10,
    stackable: false,
    category: "scholarship",
    active: true,
  },
];

/**
 * 100%-off codes were retired (they were abused). These are the only codes that
 * may still resolve to a zero payable amount. `FULLSCHOLAR100` is additionally
 * gated on an approved scholarship application (see `validate-coupon`).
 */
export const FULL_DISCOUNT_ALLOWLIST: string[] = ["WCC3604INDIA", "FULLSCHOLAR100", "BLESS100"];

/** Codes that require an `admin` role, derived from the catalogue. */
export const INTERNAL_ONLY_CODES: string[] = COUPONS.filter(
  (c) => c.internalOnly,
).map((c) => c.id);
